<template>
  <div
    class="ps-card"
    :class="{
      'ps-card--selected': selected,
      'ps-card--no-nav': !showNavButtons,
    }"
    @click="onCardClick"
  >
    <div class="ps-card__header">
      <label v-if="selectable" class="ps-card__check" @click.stop>
        <input
          type="checkbox"
          :checked="selected"
          @change="$emit('select-change', record, $event.target.checked)"
        />
      </label>
      <div class="ps-card__title-wrap">
        <span class="ps-card__name" :title="displayName">{{ displayName }}</span>
        <span
          v-if="levelText"
          class="ps-card__level"
          :class="levelClass"
        >
          <img
            v-if="levelBadgeSrc"
            class="ps-card__level-img"
            :src="levelBadgeSrc"
            alt=""
          />
          <span class="ps-card__level-text">{{ levelText }}</span>
        </span>
      </div>
      <el-popover
        v-if="showRejectTip && !isGuideRole"
        trigger="hover"
        placement="bottom-end"
        width="340"
        popper-class="ps-reject-pop"
      >
        <div
          v-for="(item, i) in rejectList"
          :key="i"
          class="ps-reject-pop__item"
        >
          <div class="ps-reject-pop__title">
            第 {{ i + 1 }} 次驳回
            <span v-if="item.levelText" class="ps-reject-pop__level">{{ item.levelText }}</span>
          </div>
          <div class="ps-reject-pop__row">驳回人：{{ item.user }}</div>
          <div class="ps-reject-pop__row">驳回时间：{{ item.time }}</div>
          <div class="ps-reject-pop__row">驳回理由：{{ item.reason }}</div>
        </div>
        <div
          slot="reference"
          class="ps-card__status"
          :class="'ps-card__status--' + statusMeta.badge"
          @click.stop
        >
          <img
            v-if="statusIcon"
            class="ps-card__status-icon"
            :class="'ps-card__status-icon--' + statusMeta.badge"
            :src="statusIcon"
            alt=""
          />
          <span>{{ statusMeta.cardText || statusMeta.text }}</span>
        </div>
      </el-popover>
      <div
        v-else-if="!isGuideRole"
        class="ps-card__status"
        :class="'ps-card__status--' + statusMeta.badge"
      >
        <img
          v-if="statusIcon"
          class="ps-card__status-icon"
          :class="'ps-card__status-icon--' + statusMeta.badge"
          :src="statusIcon"
          alt=""
        />
        <span>{{ statusMeta.cardText || statusMeta.text }}</span>
      </div>
    </div>

    <div class="ps-card__body">
      <div
        v-for="f in metaRows"
        :key="f.key"
        class="ps-card__meta-item"
        :class="{ 'ps-card__meta-item--full': f.full }"
      >
        <img
          class="ps-card__meta-icon"
          :class="'ps-card__meta-icon--' + f.icon"
          :src="iconMap[f.icon]"
          alt=""
        />
        <a-tooltip
          v-if="f.key === 'updated' && f.value && f.value !== '-'"
          :title="f.value"
        >
          <span class="ps-card__meta-text">{{ f.label }}：{{ f.value }}</span>
        </a-tooltip>
        <span v-else-if="f.key === 'version'" class="ps-card__meta-text">
          {{ f.label }}：
          <b class="ps-card__meta-version" v-if="f.value && f.value !== '-'">{{ f.value }}</b>
          <span v-else>-</span>
        </span>
        <span v-else class="ps-card__meta-text"
          >{{ f.label }}：{{ f.value }}</span
        >
      </div>
    </div>

    <div class="ps-card__foot" @click.stop>
      <button
        v-if="showPreviewButton"
        type="button"
        class="ps-card__btn"
        @click="$emit('preview', record)"
      >
        <img class="ps-card__btn-icon ps-card__btn-icon--current" :src="icons.current" alt="" />
        <span>预览</span>
      </button>
      <template v-if="showNavButtons">
        <button
          type="button"
          class="ps-card__btn"
          @click="$emit('current', record)"
        >
          <img class="ps-card__btn-icon ps-card__btn-icon--current" :src="icons.current" alt="" />
          <span>现状</span>
        </button>
        <button
          type="button"
          class="ps-card__btn"
          @click="$emit('history', record)"
          v-if="showNavExtra"
        >
          <img class="ps-card__btn-icon ps-card__btn-icon--history" :src="icons.history" alt="" />
          <span>历史</span>
        </button>
        <button
          type="button"
          class="ps-card__btn ps-card__btn--request"
          @click="$emit('requests', record)"
          v-if="showNavExtra"
        >
          <img
            class="ps-card__btn-icon"
            :class="
              requestCount > 0
                ? 'ps-card__btn-icon--request-active'
                : 'ps-card__btn-icon--request'
            "
            :src="requestIcon"
            alt=""
          />
          <span>请求</span>
          <i v-if="requestCount > 0" class="ps-card__badge">{{
            requestCount > 99 ? "99+" : requestCount
          }}</i>
        </button>
      </template>
      <slot name="actions" :record="record"></slot>
    </div>
  </div>
</template>

<script>
import {
  getModuleConfig,
  AUDIT_STATE,
  AUDIT_STATE_META,
  getCourseAuditStateMeta,
  isTwoLevelAuditModule,
  formatApplyVersion,
} from "./moduleConfig.js";
import iconLeader from "@/assets/images/projectShell/icon-leader.png";
import iconYear from "@/assets/images/projectShell/icon-year.png";
import iconVersion from "@/assets/images/projectShell/icon-version.png";
import iconUpdate from "@/assets/images/projectShell/icon-update.png";
import iconOrg from "@/assets/images/projectShell/icon-org.png";
import iconCurrent from "@/assets/images/projectShell/icon-current.png";
import iconHistory from "@/assets/images/projectShell/icon-history.png";
import iconRequest from "@/assets/images/projectShell/icon-request.png";
import iconPassed from "@/assets/images/projectShell/icon-passed.png";
import iconPending from "@/assets/images/projectShell/icon-pending.png";
import iconRequestActive from "@/assets/images/projectShell/icon-request-active.png";
import badgeProvince from "@/assets/images/projectShell/badge-province.png";
import badgeCity from "@/assets/images/projectShell/badge-city.png";
import badgeNation from "@/assets/images/projectShell/badge-nation.png";

export default {
  name: "ProjectCard",
  props: {
    moduleType: {
      type: String,
      default: "studio",
    },
    record: {
      type: Object,
      required: true,
    },
    requestCount: {
      type: Number,
      default: 0,
    },
    summary: {
      type: String,
      default: "",
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconMap: {
        leader: iconLeader,
        year: iconYear,
        version: iconVersion,
        update: iconUpdate,
        org: iconOrg,
      },
      icons: {
        current: iconCurrent,
        history: iconHistory,
        request: iconRequest,
        requestActive: iconRequestActive,
        passed: iconPassed,
        pending: iconPending,
      },
      badgeProvince,
      badgeCity,
      badgeNation,
    };
  },
  computed: {
    config() {
      return getModuleConfig(this.moduleType);
    },
    /** 教育厅角色卡片不展示状态标签（全部为审核通过） */
    isGuideRole() {
      return this.$store.state.role === "education-guide-role";
    },
    /** 只读教育侧角色（教育厅/局）不展示「历史」「请求」按钮 */
    showNavExtra() {
      const r = this.$store.state.role;
      return r !== "education-guide-role" && r !== "education-role";
    },
    moduleTitle() {
      return this.config.title || "";
    },
    displayName() {
      return this.record.name || this.record.applyName || "-";
    },
    requestIcon() {
      return this.requestCount > 0
        ? this.icons.requestActive
        : this.icons.request;
    },
    /** 工作室/微课：仅教育局/教育厅终审通过后才有现状、历史、请求 */
    showNavButtons() {
      if (isTwoLevelAuditModule(this.moduleType)) {
        return String(this.record && this.record.auditState) === AUDIT_STATE.APPROVED;
      }
      return true;
    },
    /** 工作室/微课：待教育局审核及之前用预览查看 */
    showPreviewButton() {
      if (!isTwoLevelAuditModule(this.moduleType)) return false;
      return !this.showNavButtons;
    },
    statusMeta() {
      if (isTwoLevelAuditModule(this.moduleType)) {
        return getCourseAuditStateMeta(this.record);
      }
      const key = String(this.record.auditState);
      return (
        AUDIT_STATE_META[key] || {
          text: String(this.record.auditState || "-"),
          cardText: String(this.record.auditState || "-"),
          badge: "gray",
        }
      );
    },
    statusIcon() {
      const b = this.statusMeta.badge;
      if (b === "green") return this.icons.passed;
      if (b === "orange") return this.icons.pending;
      return "";
    },
    /**
     * 有驳回数据才显示：不区分审核状态、不区分角色。
     * 优先使用后端返回的当前版本驳回记录 rejectRecords；
     * 兼容旧数据（无 rejectRecords）时，仅当卡片处于驳回状态且含驳回信息才显示，
     * 避免 updateBy/remark 等非驳回字段误触发。
     */
    hasRejectData() {
      const r = this.record || {};
      const list = Array.isArray(r.rejectRecords) ? r.rejectRecords : [];
      if (list.length) return true;
      if (String(r.auditState) !== AUDIT_STATE.REJECTED) return false;
      const info = this.rejectInfo;
      return !!(info.user || info.time || info.reason);
    },
    showRejectTip() {
      return this.hasRejectData;
    },
    rejectInfo() {
      const r = this.record || {};
      return {
        user: this.pickRecordValue(r, [
          "rejectUser",
          "rejectUserName",
          "auditUserName",
          "auditorName",
          "auditName",
          "auditBy_dictText",
          "auditBy",
          "updateBy_dictText",
          "updateBy",
        ]),
        time: this.formatRejectTime(
          this.pickRecordValue(
            r,
            ["rejectTime", "auditTime", "auditDate", "updateTime"],
            true
          )
        ),
        reason: this.pickRecordValue(r, [
          "rejectReason",
          "auditDesc",
          "auditOpinion",
          "checkReason",
          "remark",
        ]),
      };
    },
    /**
     * 当前版本下的每一次驳回记录列表。
     * 优先使用后端 getApplyOverviewList 返回的 rejectRecords（变更任务当前版本 + 申报审核）；
     * 兼容旧数据无 rejectRecords 时回退为最新一条。
     */
    rejectList() {
      const r = this.record || {};
      const list = Array.isArray(r.rejectRecords) ? r.rejectRecords : null;
      if (list && list.length) {
        return list.map((item) => ({
          user:
            item.userName ||
            item.rejectUser ||
            item.auditUserName ||
            "-",
          time: this.formatRejectTime(
            item.auditTime || item.rejectTime || item.createTime
          ),
          reason:
            item.remark || item.auditDesc || item.rejectReason || "-",
          levelText: this.rejectLevelText(item.auditLevel),
        }));
      }
      return [this.rejectInfo];
    },
    levelRaw() {
      const r = this.record || {};
      // 项目级别只信任后端 getApplyOverviewList 返回的 applyLevelText（已含微课继承父级逻辑）；
      // 不再回退到 levelType/studioLevel 等业务字段，避免 applyLevel 为空时误显示「国家级」
      const text = r.applyLevelText;
      if (text != null && String(text).trim() !== "") return text;
      if (r.applyLevel != null && String(r.applyLevel).trim() !== "") {
        return r.applyLevel;
      }
      return "";
    },
    levelText() {
      const raw = String(this.levelRaw || "").trim();
      if (raw) {
        if (
          raw.indexOf("国") >= 0 ||
          raw === "nation" ||
          raw === "national"
        )
          return "国家级";
        if (raw.indexOf("市") >= 0 || raw === "2" || raw === "city") return "市级";
        if (raw.indexOf("省") >= 0 || raw === "1" || raw === "province")
          return "省级";
        if (raw.indexOf("校") >= 0) return "校级";
        if (raw.length <= 4) return raw;
      }
      // 列表暂无级别字段，先不按 region 写死兜底，避免一律显示「省级」
      // const region = Number(this.$store && this.$store.state && this.$store.state.region);
      // if (region === 1 || region === 2 || region === 3) return "市级";
      // return "省级";
      return "";
    },
    levelClass() {
      if (this.levelText === "市级") return "ps-card__level--city";
      if (this.levelText === "国家级") return "ps-card__level--nation";
      if (this.levelText === "校级") return "ps-card__level--school";
      return "ps-card__level--province";
    },
    levelBadgeSrc() {
      if (this.levelText === "市级") return this.badgeCity;
      if (this.levelText === "省级") return this.badgeProvince;
      if (this.levelText === "国家级") return this.badgeNation;
      return "";
    },
    metaRows() {
      const fields = this.config.cardFields.slice();
      // 审核员/教育厅/局列表需要看到所属学校
      if (
        this.$store.state.role === "audit-role" ||
        this.$store.state.role === "education-role" ||
        this.$store.state.role === "education-guide-role"
      ) {
        fields.unshift({
          key: "school",
          label: "学校名称",
          from: "schoolName|realname|departname|departName|school",
          icon: "org",
          full: true,
        });
      }
      return fields.map((f) => {
        let value = this.pickValue(f.from) || "-";
        if (f.key === "updated" && value !== "-") {
          value = this.formatDateOnly(value);
        } else if (f.key === "version" && value !== "-") {
          value = formatApplyVersion(this.record);
        }
        return {
          key: f.key,
          label: f.label,
          icon: f.icon || "leader",
          full: !!f.full,
          value,
        };
      });
    },
  },
  methods: {
    onCardClick() {
      if (!this.showNavButtons) return;
      this.$emit("open", this.record);
    },
    pickValue(from) {
      if (!from || !this.record) return "";
      const keys = String(from).split("|");
      for (let i = 0; i < keys.length; i++) {
        const v = this.record[keys[i]];
        if (v !== null && v !== undefined && v !== "") return v;
      }
      return "";
    },
    pickRecordValue(record, keys, allowEmpty) {
      if (!record) return allowEmpty ? "" : "-";
      for (let i = 0; i < keys.length; i++) {
        const v = record[keys[i]];
        if (v !== null && v !== undefined && v !== "" && v !== "null") {
          return v;
        }
      }
      return allowEmpty ? "" : "-";
    },
    formatDateOnly(val) {
      if (val === null || val === undefined || val === "") return "-";
      const pad = (n) => (Number(n) < 10 ? "0" + Number(n) : String(Number(n)));
      const toYmd = (d) => {
        if (!d || Number.isNaN(d.getTime())) return "";
        return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
      };
      if (val instanceof Date) {
        return toYmd(val) || "-";
      }
      if (Array.isArray(val) && val.length >= 3) {
        return val[0] + "-" + pad(val[1]) + "-" + pad(val[2]);
      }
      if (typeof val === "number" || (/^\d+$/.test(String(val).trim()) && Number(val) > 0)) {
        const n = Number(val);
        const d = new Date(n < 1e12 ? n * 1000 : n);
        const ymd = toYmd(d);
        if (ymd) return ymd;
      }
      const s = String(val).trim();
      const m = s.match(/(\d{4})[-/.年](\d{1,2})[-/.月](\d{1,2})/);
      if (m) return m[1] + "-" + pad(m[2]) + "-" + pad(m[3]);
      const d = new Date(s.replace(/-/g, "/").replace("T", " "));
      return toYmd(d) || s.slice(0, 10);
    },
    formatRejectTime(v) {
      if (v === null || v === undefined || v === "") return "-";
      const s = String(v).trim();
      if (!s || s === "null") return "-";
      if (/^\d+$/.test(s)) {
        let n = Number(s);
        if (n < 1e12) n *= 1000;
        const d = new Date(n);
        if (isNaN(d.getTime())) return s;
        const p = (x) => (x < 10 ? "0" + x : "" + x);
        return (
          d.getFullYear() +
          "-" +
          p(d.getMonth() + 1) +
          "-" +
          p(d.getDate()) +
          " " +
          p(d.getHours()) +
          ":" +
          p(d.getMinutes()) +
          ":" +
          p(d.getSeconds())
        );
      }
      return s;
    },
    rejectLevelText(level) {
      if (level === 1 || level === "1") return "学校驳回";
      if (level === 2 || level === "2") return "教育局/厅驳回";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-card {
  box-shadow: 0 0 8px 0 rgba(115, 155, 220, 0.35);
  border-radius: 8px;
  border: 1px solid rgba(216, 235, 255, 0.96);
  background: rgba(255, 255, 255, 0.96);
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 265px;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;

  &--selected {
    border-color: #93c5fd;
    box-shadow: 0 0 10px 0 rgba(37, 99, 235, 0.35);
  }

  &--no-nav {
    cursor: default;
    &:hover {
      transform: none;
    }
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(115, 155, 220, 0.45);
    transform: translateY(-2px);
    z-index: 5;
  }

  &__check {
    position: static;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-right: 2px;
    flex-shrink: 0;
    cursor: pointer;
    line-height: 1;
    input {
      width: 16px;
      height: 16px;
      margin: 0;
      padding: 0;
      cursor: pointer;
      vertical-align: middle;
    }
  }

  &__header {
    background: #e9f3fe;
    border-radius: 8px 8px 0 0;
    height: 66px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    box-sizing: border-box;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
    height: 100%;
  }

  &__name {
    font-size: 23px;
    font-weight: 700;
    color: #0b0b0b;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    /* 级别徽标暂不展示时，不再限制标题宽度 */
    /* max-width: 200px; */
  }

  &__level {
    position: relative;
    flex-shrink: 0;
    width: 69px;
    height: 21px;
    box-sizing: border-box;
    &--school {
      width: auto;
      min-width: 54px;
      height: 21px;
      padding: 0 10px;
      border-radius: 2px 10px 2px 2px;
      background: linear-gradient(90deg, #3870ed 0%, #2563eb 100%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      .ps-card__level-text {
        position: static;
        display: inline;
        width: auto;
        height: auto;
      }
    }
  }

  &__level-img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: fill;
    pointer-events: none;
  }

  &__level-text {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    box-sizing: border-box;
    padding: 2px 0 0 8px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
    height: 21px;
    white-space: nowrap;
    &--green {
      color: #11ca08;
    }
    &--orange {
      color: #d67900;
    }
    &--red {
      color: #d10000;
    }
    &--gray,
    &--blue {
      color: #6b7280;
    }
  }

  &__status-icon {
    object-fit: contain;
    display: block;
    flex-shrink: 0;
    &--green {
      width: 15px;
      height: 15px;
    }
    &--orange {
      width: 16px;
      height: 16px;
    }
    &--red,
    &--gray,
    &--blue {
      width: 15px;
      height: 15px;
    }
  }

  &__body {
    padding: 20px 14px 8px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
    column-gap: 8px;
    row-gap: 12px;
    flex: 1;
  }

  &__meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    &--full {
      grid-column: 1 / -1;
      .ps-card__meta-text {
        white-space: normal;
        word-break: break-all;
      }
    }
  }

  &__meta-icon {
    flex-shrink: 0;
    object-fit: contain;
    display: block;
    // 与蓝湖/素材原始尺寸一致
    &--leader {
      width: 14px;
      height: 16px;
    }
    &--year {
      width: 15px;
      height: 15px;
    }
    &--version {
      width: 15px;
      height: 14px;
    }
    &--update {
      width: 14px;
      height: 14px;
    }
    &--org {
      width: 15px;
      height: 13px;
    }
  }

  &__meta-text {
    font-size: 16px;
    color: #333;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta-version {
    display: inline-block;
    padding: 1px 9px;
    margin-left: 2px;
    border-radius: 10px;
    background: #fff7e6;
    border: 1px solid #ffd591;
    color: #d46b08;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    vertical-align: middle;
  }

  &__foot {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    padding: 18px 14px 20px;
    overflow: visible;
  }

  &__btn-icon {
    object-fit: contain;
    flex-shrink: 0;
    display: block;
    line-height: 0;
    &--current {
      width: 22px;
      height: 21px;
    }
    &--history {
      width: 18px;
      height: 18px;
    }
    &--request {
      width: 16px;
      height: 17px;
    }
    &--request-active {
      width: 18px;
      height: 19px;
    }
  }

  &__btn {
    flex: 1 1 0;
    min-width: 0;
    height: 33px;
    padding: 0 4px;
    margin: 0;
    border-radius: 4px;
    border: 1px solid #3870ed;
    background: #fff;
    color: #000;
    font-size: 15px;
    font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    position: relative;
    transition: background 0.15s;
    box-sizing: border-box;
    white-space: nowrap;

    > span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      margin: 0;
      padding: 0;
      /* 中文光学居中：几何居中后字形略偏上 */
      transform: translateY(1px);
    }

    &:hover {
      background: #eff6ff;
    }

    &--request {
      overflow: visible;
    }
  }

  &__badge {
    position: absolute;
    top: -8px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 50%;
    background: #d10000;
    color: #fff;
    font-size: 13px;
    line-height: 18px;
    text-align: center;
    font-style: normal;
    box-sizing: border-box;
  }
}
</style>
<style lang="scss">
.ps-reject-pop {
  font-size: 13px;
  line-height: 1.8;
  color: #374151;
  &__item {
    padding: 6px 0;
    & + & {
      border-top: 1px dashed #dbe3ee;
    }
  }
  &__title {
    font-weight: 700;
    color: #111827;
    margin-bottom: 2px;
  }
  &__level {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 3px;
    background: #fff1f0;
    border: 1px solid #ffa39e;
    color: #d4380d;
    font-size: 12px;
    font-weight: 600;
  }
  &__row {
    word-break: break-all;
  }
}
</style>
